import React, { useState } from 'react';
import { ChartElement } from './AdvancedChart';

interface CategoryItem {
  id: string;
  name: string;
  visible: boolean;
  color?: string;
}

interface Category {
  id: string;
  name: string;
  count: number;
  expanded: boolean;
  items: CategoryItem[];
}

interface LeftControlPanelProps {
  elements: ChartElement[];
  onToggleElement: (elementId: string) => void;
  onPreToggleElement?: (elementId: string) => void;
  isDarkMode: boolean;
  chartHeight?: number;
  analysisDateModeEps?: 'as-reported' | 'at-reported';
  onAnalysisDateModeEpsChange?: (mode: 'as-reported' | 'at-reported') => void;
  analysisDateModeOcf?: 'as-reported' | 'at-reported';
  onAnalysisDateModeOcfChange?: (mode: 'as-reported' | 'at-reported') => void;
  normalizedPE?: number;
  normalizedPOCF?: number;
  sourceLabel?: string;
  sourceLabelByElementId?: Record<string, string | undefined>;
  defaultExpandedCategoryId?: string;
  showStandalonePriceItem?: boolean;
  disableAtReportedMode?: boolean;
  hiddenElementIds?: string[];
  hiddenCategoryItemKeys?: string[];
}

const LeftControlPanel: React.FC<LeftControlPanelProps> = ({
  elements,
  onToggleElement,
  onPreToggleElement,
  isDarkMode,
  chartHeight = 36,
  analysisDateModeEps = 'as-reported',
  onAnalysisDateModeEpsChange,
  analysisDateModeOcf = 'as-reported',
  onAnalysisDateModeOcfChange,
  normalizedPE = 0,
  normalizedPOCF = 0,
  sourceLabel,
  sourceLabelByElementId,
  defaultExpandedCategoryId,
  showStandalonePriceItem = false,
  disableAtReportedMode = false,
  hiddenElementIds = [],
  hiddenCategoryItemKeys = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const hiddenElementIdSet = React.useMemo(() => new Set(hiddenElementIds), [hiddenElementIds]);
  const hiddenCategoryItemKeySet = React.useMemo(() => new Set(hiddenCategoryItemKeys), [hiddenCategoryItemKeys]);
  const textColor = isDarkMode ? '#e5e7eb' : '#374151';
  const borderColor = isDarkMode ? '#374151' : '#e5e7eb';
  const sourceLabelStyle: React.CSSProperties = {
    marginLeft: '0.4rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
  };

  // Initialize categories based on elements
  const initializeCategories = (): Category[] => {
    // Price is standalone, not in any category
    const standaloneItems: CategoryItem[] = [
      { id: 'price-line', name: 'Price', visible: true, color: '#3b82f6' },
    ];

    const financialsItems: CategoryItem[] = [
      { id: 'revenue', name: 'Revenue', visible: false, color: '#10b981' },
      { id: 'gross-profit', name: 'Gross Profit', visible: false, color: '#8b5cf6' },
      { id: 'operating-income', name: 'Operating Income', visible: false, color: '#ec4899' },
      { id: 'pretax-income', name: 'Pretax Income', visible: false, color: '#06b6d4' },
      { id: 'net-income', name: 'Net Income', visible: false, color: '#f59e0b' },
      { id: 'eps-diluted', name: 'EPS (Diluted)', visible: false, color: '#10b981' },
      { id: 'cash-equivalents', name: 'Cash and Cash Equivalents', visible: false, color: '#22d3ee' },
      { id: 'total-assets', name: 'Total Assets', visible: false, color: '#14b8a6' },
      { id: 'short-term-debt', name: 'Short-Term Debt', visible: false, color: '#a855f7' },
      { id: 'long-term-debt', name: 'Long-Term Debt', visible: false, color: '#ef4444' },
      { id: 'total-liabilities', name: 'Total Liabilities', visible: false, color: '#6366f1' },
      { id: 'shareholders-equity', name: "Total Shareholders' Equity", visible: false, color: '#059669' },
      { id: 'operating-cash-flow', name: 'Operating Cash Flow', visible: false, color: '#f43f5e' },
      { id: 'free-cash-flow', name: 'Free Cash Flow', visible: false, color: '#0ea5e9' },
      { id: 'dividends', name: 'Dividends', visible: false, color: '#22c55e' },
      { id: 'buybacks', name: 'Buybacks', visible: false, color: '#eab308' },
      
    ];

    const ratiosItems: CategoryItem[] = [
      { id: 'pe', name: 'PE', visible: false, color: isDarkMode ? '#EEEEEE' : '#000000' },
      { id: 'p-ocf-ratio', name: 'P/OCF', visible: false, color: '#f472b6' },
      { id: 'p-fcf-ratio', name: 'P/FCF', visible: false, color: '#38bdf8' },
      { id: 'gross-profit-margin', name: 'Gross Profit Margin', visible: false, color: '#ec4899' },
      { id: 'operating-margin', name: 'Operating Margin', visible: false, color: '#8b5cf6' },
      { id: 'net-margin', name: 'Net Margin', visible: false, color: '#06b6d4' },
      { id: 'operating-cf-margin', name: 'Operating Cash Flow Margin', visible: false, color: '#f59e0b' },
      { id: 'free-cf-margin', name: 'Free Cash Flow Margin', visible: false, color: '#84cc16' },
      { id: 'operating-cf-to-ni', name: 'Operating Cash Flow to Net Income', visible: false, color: '#8b5cf6' },
      { id: 'free-cf-to-ni', name: 'Free Cash Flow to Net Income', visible: false, color: '#14b8a6' },
      { id: 'roic', name: 'ROIC', visible: false, color: '#a855f7' },
    ];

    const analysisItems: CategoryItem[] = [
      { id: 'pe', name: 'PE', visible: false, color: isDarkMode ? '#EEEEEE' : '#000000' },
      { id: 'flat-eps-ttm', name: 'Flat EPS (ttm)', visible: false, color: '#f97316' },
      { id: 'fair-price', name: 'Fair Price', visible: false, color: '#8b5cf6' },
      { id: 'entry-point-1', name: 'Entry Price', visible: false, color: '#10b981' },
      { id: 'entry-point-2', name: 'Exit Price', visible: false, color: '#ef4444' },
    ];

    const analysisOcfItems: CategoryItem[] = [
      { id: 'p-ocf-ratio', name: 'P/OCF', visible: false, color: '#f472b6' },
      { id: 'flat-ocf-ttm', name: 'Flat OCF/Share (ttm)', visible: false, color: '#22c55e' },
      { id: 'fair-price-ocf', name: 'Fair Price', visible: false, color: '#6366f1' },
      { id: 'entry-point-1-ocf', name: 'Entry Price', visible: false, color: '#0ea5e9' },
      { id: 'entry-point-2-ocf', name: 'Exit Price', visible: false, color: '#f97316' },
    ];

    const isExpanded = (id: string, fallback: boolean) =>
      defaultExpandedCategoryId ? id === defaultExpandedCategoryId : fallback;

    return [
      { id: 'standalone', name: '', count: 1, expanded: isExpanded('standalone', true), items: standaloneItems },
      { id: 'financials', name: 'Financials', count: 0, expanded: isExpanded('financials', false), items: financialsItems },
      { id: 'ratios', name: 'Ratios', count: 0, expanded: isExpanded('ratios', false), items: ratiosItems },
      { id: 'analysis', name: 'Analysis EPS', count: 0, expanded: isExpanded('analysis', false), items: analysisItems },
      { id: 'analysis-ocf', name: 'Analysis OCF', count: 0, expanded: isExpanded('analysis-ocf', false), items: analysisOcfItems },
    ];
  };

  const [categories, setCategories] = useState<Category[]>(initializeCategories());

  const isAnalysisItem = (itemId: string): boolean => {
    const category = categories.find(cat => cat.id === 'analysis');
    return Boolean(category?.items.some(item => item.id === itemId));
  };

  const isAnalysisOcfItem = (itemId: string): boolean => {
    const category = categories.find(cat => cat.id === 'analysis-ocf');
    return Boolean(category?.items.some(item => item.id === itemId));
  };

  const hasVisibleInCategory = (categoryId: string): boolean => {
    const category = categories.find(cat => cat.id === categoryId);
    return Boolean(category?.items.some(item => item.visible));
  };

  const isCategoryItemHidden = (categoryId: string, itemId: string): boolean => {
    if (hiddenElementIdSet.has(itemId)) return true;
    return hiddenCategoryItemKeySet.has(`${categoryId}:${itemId}`);
  };

  const isItemVisible = (itemId: string, categoryId?: string): boolean => {
    if (categoryId && isCategoryItemHidden(categoryId, itemId)) return false;
    const item = categories.flatMap(cat => cat.items).find(entry => {
      if (entry.id !== itemId) return false;
      const parentCategory = categories.find(cat => cat.items.includes(entry));
      if (!parentCategory) return true;
      return !isCategoryItemHidden(parentCategory.id, entry.id);
    });
    return Boolean(item?.visible);
  };

  // Sync categories with elements from parent whenever elements change
  React.useEffect(() => {
    setCategories(prev => 
      prev.map(cat => ({
        ...cat,
        items: cat.items.map(item => {
          // Find matching element from props
          const matchingElement = elements.find(el => el.id === item.id);
          if (matchingElement) {
            return { ...item, visible: matchingElement.visible };
          }
          return item;
        }),
        count: cat.items.filter(item => {
          if (isCategoryItemHidden(cat.id, item.id)) return false;
          const matchingElement = elements.find(el => el.id === item.id);
          return matchingElement ? matchingElement.visible : item.visible;
        }).length
      }))
    );
  }, [elements, hiddenElementIdSet, hiddenCategoryItemKeySet]);

  const toggleCategory = (categoryId: string) => {
    setCategories(prev => 
      prev.map(cat => 
        cat.id === categoryId 
          ? { ...cat, expanded: !cat.expanded }
          : cat
      )
    );
  };

  const isLockedWhenNormalizedPEZero = (itemId: string): boolean => {
    if (itemId === 'fair-price' || itemId === 'entry-point-1' || itemId === 'entry-point-2') {
      return !(normalizedPE > 0);
    }
    if (itemId === 'fair-price-ocf' || itemId === 'entry-point-1-ocf' || itemId === 'entry-point-2-ocf') {
      return !(normalizedPOCF > 0);
    }
    return false;
  };

  const isToggleDisabled = (itemId: string, categoryId?: string): boolean => {
    if (isLockedWhenNormalizedPEZero(itemId)) return true;

    const isVisible = isItemVisible(itemId);
    if (!isVisible) {
      const isEpsEntryExit = itemId === 'entry-point-1' || itemId === 'entry-point-2';
      const isOcfEntryExit = itemId === 'entry-point-1-ocf' || itemId === 'entry-point-2-ocf';
      const hasEpsEntryExit = isItemVisible('entry-point-1') || isItemVisible('entry-point-2');
      const hasOcfEntryExit = isItemVisible('entry-point-1-ocf') || isItemVisible('entry-point-2-ocf');

      if (isEpsEntryExit && hasOcfEntryExit) return true;
      if (isOcfEntryExit && hasEpsEntryExit) return true;

    }

    return false;
  };

  const handleToggleItem = (itemId: string, categoryId?: string) => {
    if (isToggleDisabled(itemId, categoryId)) {
      return;
    }
    // Map UI item IDs to chart element IDs
    const idMapping: { [key: string]: string } = {
      'price-line': 'price-line',
      'revenue': 'revenue',
      'net-income': 'net-income',
      'pe': 'pe'
    };

    const chartElementId = idMapping[itemId] || itemId;

    // Find the item to check if we're trying to enable it
    const itemToToggle = categories
      .flatMap(cat => cat.items)
      .find(item => item.id === itemId);
    const willEnable = itemToToggle ? !itemToToggle.visible : false;

    // Count currently visible items across all categories
    const currentVisibleIds = new Set(
      categories
        .flatMap(cat => cat.items.map(item => ({ item, categoryId: cat.id })))
        .filter(({ item, categoryId }) => !isCategoryItemHidden(categoryId, item.id) && item.visible)
        .map(({ item }) => item.id)
    );
    const currentVisibleCount = currentVisibleIds.size;

    console.log('🔶 Toggle attempt:', {
      itemId,
      itemToToggle,
      currentVisibleCount,
      willBeVisible: itemToToggle ? !itemToToggle.visible : 'unknown',
      maxReached: currentVisibleCount >= 5
    });

    // If trying to enable and already at max (5), don't allow
    if (itemToToggle && !itemToToggle.visible && currentVisibleCount >= 5) {
      console.log('🚫 Blocked: Maximum 5 items reached');
      return; // Don't allow more than 5 visible items
    }

    onPreToggleElement?.(chartElementId);

    // Update local state and count
    setCategories(prev => 
      prev.map(cat => {
        const updatedItems = cat.items.map(item => 
          item.id === itemId 
            ? { ...item, visible: !item.visible }
            : item
        );
        
        // Calculate new count based on visible items
        const newCount = updatedItems.filter(item => item.visible && !isCategoryItemHidden(cat.id, item.id)).length;
        
        return {
          ...cat,
          items: updatedItems,
          count: newCount
        };
      })
    );
    
    // Call parent handler if it's a chart element
    onToggleElement(chartElementId);
  };

  const categoriesForDisplay = categories
    .map(category => ({
      ...category,
      items: category.items.filter(item => !isCategoryItemHidden(category.id, item.id)),
    }))
    .filter(category => category.id === 'standalone' || category.items.length > 0);

  // Filter categories based on search query
  const filteredCategories = categoriesForDisplay.map(category => ({
    ...category,
    items: category.items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.items.length > 0);

  return (
    <>
      <style>{`
        #categories-container::-webkit-scrollbar {
          width: 8px;
        }
        #categories-container::-webkit-scrollbar-track {
          background: transparent;
        }
        #categories-container::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 2px;
        }
        #categories-container::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
        #search-box-container input::placeholder {
          color: ${isDarkMode ? '#FFFFFF' : '#728197'};
        }
      `}</style>
      <div
        id="left-control-panel"
        style={{
          width: '250px',
          backgroundColor: isDarkMode ? '#111827' : '#FDFDFF',
          borderRadius: '0rem',
          padding: '0.75rem 0.4375rem',
          border: `0px solid ${borderColor}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
      {/* Search Box */}
      <div
        id="search-box-container"
        style={{
          marginBottom: '12px',
          position: 'relative',
          marginTop: '-10px',
          backgroundColor: isDarkMode ? '#111827' : 'transparent',
          borderRadius: '0.375rem',
        }}
      >
        <div id="search-icon-container" style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: isDarkMode ? '#FFFFFF' : '#6b7280',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem 0.5rem 2rem',
            fontSize: '0.75rem',
            border: `1px solid ${isDarkMode ? '#374151' : borderColor}`,
            borderRadius: '0.375rem',
            outline: 'none',
            backgroundColor: isDarkMode ? '#111827' : '#ffffff',
            color: isDarkMode ? '#FFFFFF' : '#728197',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div
        id="categories-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isDarkMode ? '0' : '0.5rem',
          flex: '0 0 auto',
          height: 'calc(var(--chart-panel-height) + 85px)',
          maxHeight: 'calc(var(--chart-panel-height) + 85px)',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        {showStandalonePriceItem && !searchQuery && (() => {
          const priceElement = elements.find((el) => el.id === 'price-line');
          if (!priceElement) return null;
          const disabled = isToggleDisabled('price-line');
          const label = sourceLabelByElementId?.['price-line'] ?? sourceLabel;
          return (
            <div
              id="standalone-price-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.375rem 0.5rem',
                borderRadius: isDarkMode ? '0' : '0.25rem',
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: priceElement.visible
                  ? (isDarkMode ? '#374151' : '#F1F7FF')
                  : 'transparent',
                transition: 'background-color 0.2s ease',
                opacity: disabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (!disabled) handleToggleItem('price-line');
              }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#F1F7FF';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = priceElement.visible
                  ? (isDarkMode ? '#374151' : '#F1F7FF')
                  : 'transparent';
              }}
            >
              <div
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '0.1875rem',
                  border: `2px solid ${priceElement.visible ? (isDarkMode ? '#3B82F6' : '#2F58F2') : '#9ca3af'}`,
                  backgroundColor: priceElement.visible ? (isDarkMode ? '#3B82F6' : '#2F58F2') : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                {priceElement.visible && (
                  <span style={{ color: isDarkMode ? '#374151' : '#ffffff', fontSize: '0.625rem', fontWeight: 'bold' }}>
                    ✓
                  </span>
                )}
              </div>
              <span
                style={{
                  color: isDarkMode ? '#3B82F6' : (priceElement.visible ? '#2F58F2' : textColor),
                  fontSize: '1rem',
                  fontWeight: '500',
                  flex: 1,
                }}
              >
                Price
                {priceElement.visible && label ? <span style={sourceLabelStyle}>{label}</span> : null}
              </span>
              {priceElement.color && (
                <div
                  style={{
                    width: '6px',
                    height: '0.375rem',
                    borderRadius: '50%',
                    backgroundColor: priceElement.color,
                    border: '1px solid #ffffff',
                  }}
                />
              )}
              <div
                style={{
                  width: '14px',
                  height: '14px',
                  padding: '0.125rem',
                }}
              />
            </div>
          );
        })()}
        {searchQuery ? (
          // When searching, show flat list without categories
          <div id="search-results-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {filteredCategories.flatMap(category => 
              category.items.map((item) => (
                  (() => {
                    const disabled = isToggleDisabled(item.id, category.id);
                    return (
                  <div
                    id={`search-item-${item.id}`}
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.375rem 0.5rem',
                      borderRadius: isDarkMode ? '0' : '0.25rem',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      backgroundColor: item.visible
                        ? (isDarkMode ? '#374151' : '#F1F7FF')
                        : 'transparent',
                      transition: 'background-color 0.2s ease',
                      opacity: disabled ? 0.5 : 1,
                    }}
                    onClick={() => {
                      if (!disabled) handleToggleItem(item.id, category.id);
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#F1F7FF';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = item.visible
                        ? (isDarkMode ? '#374151' : '#F1F7FF')
                        : 'transparent';
                    }}
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '0.1875rem',
                        border: `2px solid ${item.visible ? (isDarkMode ? '#3B82F6' : '#2F58F2') : '#9ca3af'}`,
                        backgroundColor: item.visible ? (isDarkMode ? '#3B82F6' : '#2F58F2') : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {item.visible && (
                        <span style={{ color: isDarkMode ? '#374151' : '#ffffff', fontSize: '0.625rem', fontWeight: 'bold' }}>
                          ✓
                        </span>
                      )}
                    </div>

                    {/* Item name */}
                    <span
                      style={{
                        color: isDarkMode ? '#3B82F6' : (item.visible ? '#2F58F2' : textColor),
                        fontSize: '1rem',
                        fontWeight: '500',
                        flex: 1,
                      }}
                    >
                      {item.name}
                      {(() => {
                        const label = sourceLabelByElementId?.[item.id] ?? sourceLabel;
                        return item.visible && label ? <span style={sourceLabelStyle}>{label}</span> : null;
                      })()}
                    </span>

                    {/* Color indicator for chart elements */}
                    {item.color && (
                      <div
                        style={{
                          width: '6px',
                          height: '0.375rem',
                          borderRadius: '50%',
                          backgroundColor: item.color,
                          border: '1px solid #ffffff',
                        }}
                      />
                    )}

                    {/* Spacer to preserve layout (menu removed) */}
                    <div
                      style={{
                        width: '14px',
                        height: '14px',
                        padding: '0.125rem',
                      }}
                    />
                  </div>
                    );
                  })()
              ))
            )}
          </div>
        ) : (
          // Normal view with categories
          filteredCategories.map((category) => (
            <div id={`category-${category.id}`} key={category.id}>
              {/* Category Header - Only show if category has a name */}
              {category.name && (
                <div
                  id={`category-header-${category.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: isDarkMode ? '0.375rem 0.5rem' : '0.375rem 0.25rem',
                    cursor: 'pointer',
                    color: textColor,
                    fontSize: '1rem',
                    fontWeight: '500',
                    backgroundColor: isDarkMode ? '#374151' : 'transparent',
                    borderRadius: isDarkMode ? '0' : '0.25rem',
                  }}
                  onClick={() => toggleCategory(category.id)}
                >
                  <div id={`category-name-${category.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{category.name}</span>
                    {category.count > 0 && (
                      <span
                        style={{
                          backgroundColor: isDarkMode ? '#1F2937' : '#D9D9D9',
                          color: isDarkMode ? '#3B82F6' : '#2F58F2',
                          borderRadius: '0.25rem',
                          padding: '0.0625rem 0.3125rem',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {category.count}
                      </span>
                    )}
                  </div>
                  <span style={{ 
                    transform: category.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="19" height="24" viewBox="0 0 19 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.54175 10L9.50008 14L13.4584 10" stroke={isDarkMode ? '#3B82F6' : '#728197'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </div>
              )}

              {/* Category Items */}
              {category.expanded && (
                <div
                  id={`category-items-${category.id}`}
                  style={{
                    marginLeft: isDarkMode ? '0' : (category.name ? '8px' : '0'),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isDarkMode ? '0' : '0.25rem',
                  }}
                >
                  {(category.id === 'analysis' || category.id === 'analysis-ocf') && (
                    <div
                      id="analysis-date-mode"
                      style={{
                        padding: '0.5rem 0',
                        marginBottom: '0.25rem',
                        marginLeft: isDarkMode ? '0.5rem' : '-0.25rem',
                        marginRight: isDarkMode ? '0.5rem' : '0.25rem',
                        borderTop: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
                      }}
                    >
                      {(() => {
                        const isEpsCategory = category.id === 'analysis';
                        const analysisDateMode = isEpsCategory ? analysisDateModeEps : analysisDateModeOcf;
                        const onAnalysisDateModeChange = isEpsCategory
                          ? onAnalysisDateModeEpsChange
                          : onAnalysisDateModeOcfChange;
                        return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: textColor }}>As Reported</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={analysisDateMode === 'at-reported'}
                          disabled={disableAtReportedMode}
                          onClick={() =>
                            !disableAtReportedMode &&
                            onAnalysisDateModeChange?.(
                              analysisDateMode === 'as-reported' ? 'at-reported' : 'as-reported'
                            )
                          }
                          style={{
                            width: '36px',
                            height: '22px',
                            borderRadius: '16px',
                            background: '#F1F7FF',
                            border: '1px solid #D9D9D9',
                            padding: 0,
                            position: 'relative',
                            cursor: disableAtReportedMode ? 'not-allowed' : 'pointer',
                            opacity: disableAtReportedMode ? 0.55 : 1,
                          }}
                          aria-label="Toggle analysis date mode"
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: '0px',
                              left: analysisDateMode === 'at-reported' ? '14.53px' : '0.42px',
                              width: '19.058822631835938px',
                              height: '19.161291122436523px',
                              borderRadius: '16px',
                              background: '#2F58F2',
                              boxShadow: '0px 3px 7px 0px #0000001F',
                              transition: 'left 160ms ease',
                            }}
                          />
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: textColor }}>At Reported</span>
                      </div>
                        );
                      })()}
                    </div>
                  )}
                  {category.items.map((item) => (
                  (() => {
                    const disabled = isToggleDisabled(item.id, category.id);
                    return (
                      <div
                        id={`item-${item.id}`}
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.375rem 0.5rem',
                          borderRadius: isDarkMode ? '0' : '0.25rem',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          backgroundColor: item.visible
                            ? (isDarkMode ? '#374151' : '#F1F7FF')
                            : 'transparent',
                          transition: 'background-color 0.2s ease',
                          opacity: disabled ? 0.5 : 1,
                        }}
                        onClick={() => {
                          if (!disabled) handleToggleItem(item.id, category.id);
                        }}
                        onMouseEnter={(e) => {
                          if (!disabled) {
                            e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#F1F7FF';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = item.visible
                            ? (isDarkMode ? '#374151' : '#F1F7FF')
                            : 'transparent';
                        }}
                      >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '0.1875rem',
                        border: `2px solid ${item.visible ? (isDarkMode ? '#3B82F6' : '#2F58F2') : '#9ca3af'}`,
                        backgroundColor: item.visible ? (isDarkMode ? '#3B82F6' : '#2F58F2') : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {item.visible && (
                        <span style={{ color: isDarkMode ? '#374151' : '#ffffff', fontSize: '0.625rem', fontWeight: 'bold' }}>
                          ✓
                        </span>
                      )}
                    </div>

                    {/* Item name */}
                    <span
                      style={{
                        color: isDarkMode ? '#3B82F6' : (item.visible ? '#2F58F2' : textColor),
                        fontSize: '1rem',
                        fontWeight: '500',
                        flex: 1,
                      }}
                    >
                      {item.name}
                      {(() => {
                        const label = sourceLabelByElementId?.[item.id] ?? sourceLabel;
                        return item.visible && label ? <span style={sourceLabelStyle}>{label}</span> : null;
                      })()}
                    </span>

                    {/* Color indicator for chart elements */}
                    {item.color && (
                      <div
                        style={{
                          width: '6px',
                          height: '0.375rem',
                          borderRadius: '50%',
                          backgroundColor: item.color,
                          border: '1px solid #ffffff',
                        }}
                      />
                    )}

                    {/* Spacer to preserve layout (menu removed) */}
                    <div
                      style={{
                        width: '14px',
                        height: '14px',
                        padding: '0.125rem',
                      }}
                    />
                      </div>
                    );
                  })()
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
    </>
  );
};

export default LeftControlPanel;





